// src/services/waiverApi.js
// Waiver wire logic: priority queue, window enforcement, claim processing.
// Priority order = reverse standings (worst record picks first).
// Window: opens WAIVER_OPEN_HOURS before week start, closes at week start.

import { ref, get, update, push, onValue } from "firebase/database";

export const WAIVER_OPEN_HOURS  = 24;   // window opens 24h before week
export const MAX_SQUAD          = 15;
export const ROLE_LIMITS        = { BAT: 7, BOWL: 7, ALR: 5, WK: 4 };

// ── Helpers ────────────────────────────────────────────────────

export function waiverWindowOpen(league) {
  return true; // always open
}

export function waiverWindowLabel(league) {
  return "Always open";
}

// ── Priority calculation ────────────────────────────────────────
// Returns array of team names sorted by waiver priority (worst record first).
// Tiebreak: higher waiver order number (set on claim, cycles after each claim).

export function calcWaiverOrder(league) {
  const schedule = league?.season?.schedule || {};
  const waiverOrder = league?.season?.waiverOrder || {};   // { teamName: orderNum }

  // Compute W-L from schedule
  const records = {};
  const ensureTeam = (t) => { if (!records[t]) records[t] = { w: 0, l: 0, t: 0, pf: 0 }; };

  Object.values(schedule).forEach(week => {
    (week?.matchups || []).forEach(m => {
      if (!m.home || !m.away) return;
      const hp = Number(m.homePoints), ap = Number(m.awayPoints);
      if (!Number.isFinite(hp) || !Number.isFinite(ap)) return;
      ensureTeam(m.home); ensureTeam(m.away);
      records[m.home].pf += hp; records[m.away].pf += ap;
      if (hp > ap) { records[m.home].w++; records[m.away].l++; }
      else if (ap > hp) { records[m.away].w++; records[m.home].l++; }
      else { records[m.home].t++; records[m.away].t++; }
    });
  });

  const allTeams = Object.keys(waiverOrder).length
    ? Object.keys(waiverOrder)
    : Object.keys(records);

  return allTeams.slice().sort((a, b) => {
    const ra = records[a] || { w: 0, l: 0, t: 0, pf: 0 };
    const rb = records[b] || { w: 0, l: 0, t: 0, pf: 0 };
    // Worst record first
    const winPctA = ra.w / Math.max(1, ra.w + ra.l + ra.t);
    const winPctB = rb.w / Math.max(1, rb.w + rb.l + rb.t);
    if (winPctA !== winPctB) return winPctA - winPctB;
    if (ra.pf !== rb.pf) return ra.pf - rb.pf;
    // Fallback: waiver order number (lower = higher priority)
    return (waiverOrder[a] ?? 999) - (waiverOrder[b] ?? 999);
  });
}

// ── Submit a waiver claim ───────────────────────────────────────
// claimData: { teamName, addPlayerId, dropPlayerId, week }
export async function submitWaiverClaim(db, leagueCode, claimData) {
  const code = leagueCode.toUpperCase();
  const claimsRef = ref(db, `leagues/${code}/season/waiverClaims`);
  const newRef = push(claimsRef);
  await update(ref(db), {
    [`leagues/${code}/season/waiverClaims/${newRef.key}`]: {
      ...claimData,
      status: "pending",
      submittedAt: Date.now(),
      id: newRef.key,
    },
  });
  return newRef.key;
}

// ── Cancel a claim ──────────────────────────────────────────────
export async function cancelWaiverClaim(db, leagueCode, claimId) {
  const code = leagueCode.toUpperCase();
  await update(ref(db), {
    [`leagues/${code}/season/waiverClaims/${claimId}/status`]: "cancelled",
  });
}

// ── Process all pending claims (host only) ─────────────────────
// Runs through claims in priority order, executes valid ones, rejects duplicates.
export async function processWaiverClaims(db, leagueCode) {
  const code = leagueCode.toUpperCase();
  const [leagueSnap, roomSnap] = await Promise.all([
    get(ref(db, `leagues/${code}`)),
    get(ref(db, `rooms/${code}`)),
  ]);
  const league = leagueSnap.val();
  const room   = roomSnap.val();
  if (!league || !room) throw new Error("League/room not found");

  const rawClaims = league?.season?.waiverClaims || {};
  const pending = Object.values(rawClaims)
    .filter(c => c.status === "pending")
    .sort((a, b) => a.submittedAt - b.submittedAt);

  if (!pending.length) return { processed: 0, results: [] };

  const priority = calcWaiverOrder(league);
  // Sort pending by priority rank, then submission time
  pending.sort((a, b) => {
    const pa = priority.indexOf(a.teamName);
    const pb = priority.indexOf(b.teamName);
    if (pa !== pb) return pa - pb;
    return a.submittedAt - b.submittedAt;
  });

  const playerMap   = room.players || {};
  const teams       = room.teams   || [];
  const addedThisRun = new Set();  // players claimed this run
  const results     = [];
  const dbUpdates   = {};

  // Track which teams already used their claim (one claim per team per window)
  const teamClaimedThisRun = new Set();

  for (const claim of pending) {
    const { id, teamName, addPlayerId, dropPlayerId } = claim;
    const team = teams.find(t => t.name === teamName);
    if (!team) { dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_no_team"; results.push({ id, result: "rejected", reason: "Team not found" }); continue; }

    const squad    = [...(team.squadIds || [])].map(String);
    const addIdStr = String(addPlayerId);
    const addPlayer = playerMap[addIdStr];

    // Reject: already claimed by another team this run
    if (addedThisRun.has(addIdStr)) {
      dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_claimed";
      results.push({ id, result: "rejected", reason: "Player already claimed by another team" });
      continue;
    }

    // Reject: team already used a claim this processing run
    if (teamClaimedThisRun.has(teamName)) {
      dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_one_per_window";
      results.push({ id, result: "rejected", reason: "One successful claim per waiver window" });
      continue;
    }

    // Reject: player already on a squad
    const alreadyOwned = teams.some(t => (t.squadIds || []).map(String).includes(addIdStr));
    if (alreadyOwned) {
      dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_owned";
      results.push({ id, result: "rejected", reason: "Player is already rostered" });
      continue;
    }

    // Validate drop
    const dropIdStr = dropPlayerId ? String(dropPlayerId) : null;
    if (!squad.includes(addIdStr)) {
      // Need to drop to make room if at max
      if (squad.length >= MAX_SQUAD && !dropIdStr) {
        dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_squad_full";
        results.push({ id, result: "rejected", reason: "Squad full — must drop a player" });
        continue;
      }
      if (dropIdStr && !squad.includes(dropIdStr)) {
        dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_bad_drop";
        results.push({ id, result: "rejected", reason: "Drop target not on your roster" });
        continue;
      }
    }

    // Role limit check
    const addRole = addPlayer?.role;
    if (addRole) {
      const limit = ROLE_LIMITS[addRole];
      if (limit !== undefined) {
        const currentCount = squad.filter(id => playerMap[id]?.role === addRole).length;
        const dropRole = dropIdStr ? playerMap[dropIdStr]?.role : null;
        const afterDrop = dropRole === addRole ? currentCount - 1 : currentCount;
        if (afterDrop >= limit) {
          dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`] = "rejected_role_limit";
          results.push({ id, result: "rejected", reason: `${addRole} limit (${limit}) reached` });
          continue;
        }
      }
    }

    // ✅ Execute the claim
    const newSquad = squad.filter(id => id !== dropIdStr);
    newSquad.push(addIdStr);

    const teamIdx = teams.findIndex(t => t.name === teamName);
    dbUpdates[`rooms/${code}/teams/${teamIdx}/squadIds`] = newSquad.map(Number);
    if (addPlayer) dbUpdates[`rooms/${code}/players/${addIdStr}`] = addPlayer;

    dbUpdates[`leagues/${code}/season/waiverClaims/${id}/status`]      = "approved";
    dbUpdates[`leagues/${code}/season/waiverClaims/${id}/processedAt`] = Date.now();

    addedThisRun.add(addIdStr);
    teamClaimedThisRun.add(teamName);
    results.push({ id, result: "approved", teamName, addPlayer: addPlayer?.name, dropId: dropIdStr });
  }

  // Rotate waiver priority: teams that successfully claimed go to the back
  const currentOrder = calcWaiverOrder(league);
  const newOrder = {};
  let idx = 0;
  // Non-claimers keep their relative order first
  for (const t of currentOrder) {
    if (!teamClaimedThisRun.has(t)) newOrder[t] = idx++;
  }
  // Claimers go to the back
  for (const t of currentOrder) {
    if (teamClaimedThisRun.has(t)) newOrder[t] = idx++;
  }
  dbUpdates[`leagues/${code}/season/waiverOrder`] = newOrder;
  dbUpdates[`leagues/${code}/season/lastWaiverProcessed`] = Date.now();

  await update(ref(db), dbUpdates);
  return { processed: results.filter(r => r.result === "approved").length, results };
}

// ── Subscribe to claims for a team ─────────────────────────────
export function subscribeWaiverClaims(db, leagueCode, callback) {
  const code = leagueCode.toUpperCase();
  return onValue(ref(db, `leagues/${code}/season/waiverClaims`), snap => {
    callback(snap.val() || {});
  });
}
